import { Avatar, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { Badge } from 'flowbite-react';
import React, { useCallback, useEffect, useState } from 'react';
import { HiCheckCircle } from 'react-icons/hi';
import { stringAvatar } from 'shared-utils';
import { generateCompanyLogoAvatarSrc } from 'src/utils/companyLogo';
import { MyCompanyType, PositionStatus } from 'types';

const AMCompanyCardTitle = ({
	isActive,
	companyData,
	isBlacklisted,
}: {
	isActive?: boolean;
	isBlacklisted?: boolean;
	companyData: MyCompanyType;
	activePositions?: number;
}) => {
	const [avatarImg, setAvatarImg] = useState('');

	const isUpdatedWithinTwoDays = dayjs().diff(dayjs(companyData?.company?.createdAt), 'day') <= 1;
	const isLead = companyData.company.isLead;
	const isQualified = companyData.company.isSelf && companyData.company.isQualifiedLead;
	const isDisQualified = companyData.company.isDisQualifiedLead;
	const selfCreatedPosition =
		companyData.company.isSelf && !companyData.company.isQualifiedLead && companyData.totalPositions;

	const generateAvatar = useCallback(async () => {
		const res = await generateCompanyLogoAvatarSrc(companyData?.company?.logo || '', companyData?.company?.url);
		setAvatarImg(res);
	}, [companyData?.company?.logo, companyData?.company?.url]);

	useEffect(() => {
		generateAvatar();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyData?.company?.logo, companyData?.company?.url]);

	const isArchived = Array.isArray(companyData?.company?.leadGenMeta) && companyData?.company?.leadGenMeta[0]?.leadGen?.isArchived
		? companyData?.company?.leadGenMeta[0]?.leadGen?.isArchived
		: false;

	return (
		<div>
			<div className="flex gap-2">
				<div className="absolute top-[-11px] right-[10px] flex flex-wrap items-center gap-2">
					{isLead ? (
						<Tooltip
							title={dayjs(companyData?.company?.createdAt).format('YYYY-MM-DDTHH:mm:ssZ[Z]')}
							placement="bottom"
						>
							<Badge className="text-[#42389D] bg-[#E5EDFF] rounded-[6px]">New Lead</Badge>
						</Tooltip>
					) : selfCreatedPosition ? (
						<Tooltip
							title={dayjs(companyData?.company?.createdAt).format('YYYY-MM-DDTHH:mm:ssZ[Z]')}
							placement="bottom"
						>
							<Badge className="text-yellow-500 bg-yellow-200 rounded-[6px]">Self Created Position</Badge>
						</Tooltip>
					) : isQualified ? (
						<Tooltip
							title={dayjs(companyData?.company?.createdAt).format('YYYY-MM-DDTHH:mm:ssZ[Z]')}
							placement="bottom"
						>
							<Badge className="text-green-800 bg-green-200 rounded-[6px]">Qualified Lead</Badge>
						</Tooltip>
					) : isDisQualified ? (
						<Tooltip
							title={dayjs(companyData?.company?.createdAt).format('YYYY-MM-DDTHH:mm:ssZ[Z]')}
							placement="bottom"
						>
							<Badge className="text-red-800 bg-red-200 rounded-[6px]">Disqualified Lead</Badge>
						</Tooltip>
					) : isUpdatedWithinTwoDays && !isArchived ? (
						<Tooltip
							title={dayjs(companyData?.company?.createdAt).format('YYYY-MM-DDTHH:mm:ssZ[Z]')}
							placement="bottom"
						>
							<Badge className="text-[#42389D] bg-[#E5EDFF] rounded-[6px]">Newly Created</Badge>
						</Tooltip>
					) : null}

					{isActive && companyData?.activePositions &&
						companyData?.company?.positions?.filter(position =>
							position?.positionStatus !== PositionStatus.CLOSED
						).length ? (
						<Badge className="bg-[#E3FFF1] text-[#03543F] py-[3px] rounded-[6px]" icon={HiCheckCircle}>
							Active
						</Badge>
					) : null}

					{isBlacklisted &&
						companyData?.company?.positions?.filter(position => position?.positionStatus !== PositionStatus.CLOSED)
							.length ? (
						<Badge className="bg-red-700 text-white py-[3px] rounded-[6px]">Blacklisted</Badge>
					) : null}
				</div>

				<div className="flex justify-center items-center text-center min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] rounded-full border border-[#F3F2F2]">
					<Avatar
						alt="companyLogo"
						src={avatarImg}
						sx={{ width: '25.6px', height: '25.6px', minWidth: '25.6px', minHeight: '25.6px', fontSize: '12px' }}
					>
						{stringAvatar(companyData?.company?.name.trim().toUpperCase() || '')}
					</Avatar>
				</div>
				<div className="w-full">
					<div className="flex gap-3 w-full">
						<h3 className="text-base md:text-xl font-medium md:font-semibold max-w-[80%] truncate font-inter dark:text-white text-[#1C1C28]">
							{companyData?.company?.name}
						</h3>
					</div>

					<div className="flex flex-row gap-2 items-center">
						<div className="flex gap-1 items-center sm:text-[12px] text-[14px] text-[#28293D] dark:text-[#E4E4EB]">
							{companyData?.company?.headQuarter ? (
								<div className="dark:text-[#28293D]">{companyData?.company?.headQuarter}</div>
							) : null}
							{companyData?.company?.headQuarter && companyData?.company?.nature ? (
								<div className="font-inter font-[500] leading-[16px] text-[#28293D] dark:text-[#C7C9D9]">•</div>
							) : null}
							{companyData?.company?.nature ? (
								<div className="font-medium text-[#28293D]">{companyData?.company?.nature}</div>
							) : null}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AMCompanyCardTitle;
